import { Anchor, Box, Card, Center, Group, LoadingOverlay, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import {
  IconBell,
  IconBrandFacebook,
  IconBrandGoogle,
  IconBrandWhatsapp,
  IconChartBarOff,
  IconDeviceMobile,
  IconMail,
  IconPhone,
  IconWebhook,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { dateToEndOfDay, dateToStartOfDay } from '../../helpers/dateUtils';
import useFetch from '../../helpers/useFetch';

const countCol = (stat, label, opts = {}) => ({ stat, label, statKey: 'count', ...opts });
const currencyCol = (stat, label) => ({ stat, label, statKey: 'value', currency: true });

// Merge key for unified columns — must stay identical wherever columns are
// merged (ChannelBreakdownTable + StepBreakdown) or the two tables' columns
// won't line up.
const columnKey = (col) => `${col.label}|${col.stat}|${col.statKey}|${col.currency ? 'c' : ''}`;

const CHANNELS = [
  {
    nodeKey: 'sendEmail',
    scope: 'emails',
    urlSlug: 'emails',
    label: 'Email',
    Icon: IconMail,
    columns: [
      countCol('sent', 'Sent'),
      countCol('deliveries', 'Delivered'),
      countCol('opens', 'Opened'),
      countCol('clicks', 'Clicks'),
      countCol('purchases', 'Purchases'),
      currencyCol('purchases', 'Revenue'),
      countCol('bounces', 'Bounces'),
      countCol('complaints', 'Complaints'),
      countCol('unsubscribes', 'Unsubscribes'),
    ],
  },
  {
    nodeKey: 'sendPushNotification',
    scope: 'pushNotifications',
    urlSlug: 'push',
    label: 'Push',
    Icon: IconBell,
    columns: [
      countCol('sent', 'Sent'),
      countCol('deliveries', 'Delivered'),
      countCol('clicks', 'Clicks'),
      countCol('conversions', 'Conversions'),
      countCol('purchases', 'Purchases'),
      currencyCol('purchases', 'Revenue'),
      countCol('bounces', 'Bounces'),
    ],
  },
  {
    nodeKey: 'sendTextMessage',
    scope: 'textMessages',
    urlSlug: 'textMessages',
    label: 'SMS',
    Icon: IconDeviceMobile,
    columns: [
      countCol('sent', 'Sent'),
      countCol('deliveries', 'Delivered'),
      countCol('clicks', 'Clicks'),
      countCol('conversions', 'Conversions'),
      countCol('purchases', 'Purchases'),
      currencyCol('purchases', 'Revenue'),
      countCol('unsubscribes', 'Unsubscribes'),
      countCol('bounces', 'Bounces'),
    ],
  },
  {
    nodeKey: 'sendViberMessage',
    scope: 'viberMessages',
    urlSlug: 'viber',
    label: 'Viber',
    Icon: IconPhone,
    columns: [
      countCol('sent', 'Sent'),
      countCol('deliveries', 'Delivered'),
      countCol('opens', 'Opened'),
      countCol('clicks', 'Clicks'),
      countCol('conversions', 'Conversions'),
      countCol('purchases', 'Purchases'),
      currencyCol('purchases', 'Revenue'),
      countCol('unsubscribes', 'Unsubscribes'),
      countCol('bounces', 'Bounces'),
    ],
  },
  {
    nodeKey: 'sendWhatsappMessage',
    scope: 'whatsappMessages',
    urlSlug: 'whatsapp',
    label: 'WhatsApp',
    Icon: IconBrandWhatsapp,
    columns: [
      countCol('sent', 'Sent'),
      countCol('deliveries', 'Delivered'),
      countCol('opens', 'Opened'),
      countCol('clicks', 'Clicks'),
      countCol('conversions', 'Conversions'),
      countCol('purchases', 'Purchases'),
      currencyCol('purchases', 'Revenue'),
      countCol('bounces', 'Bounces'),
    ],
  },
  {
    nodeKey: 'sendWebhook',
    scope: 'webhooks',
    urlSlug: 'webhooks',
    label: 'Webhook',
    Icon: IconWebhook,
    columns: [
      countCol('sent', 'Sent'),
      countCol('deliveries', 'Delivered'),
      countCol('clicks', 'Clicks'),
      countCol('conversions', 'Conversions'),
      countCol('purchases', 'Purchases'),
      currencyCol('purchases', 'Revenue'),
      countCol('skipped', 'Skipped'),
      countCol('bounces', 'Bounces'),
    ],
  },
  // FB / Google use UTM-attributed events — per-Releva-campaign correct but
  // no impressions / spend / engagements (those live on the ad-network side).
  {
    nodeKey: 'sendFacebookAd',
    scope: 'facebookAds',
    urlSlug: null,
    label: 'Facebook Ads',
    Icon: IconBrandFacebook,
    columns: [
      countCol('clicks', 'Clicks'),
      countCol('conversions', 'Conversions'),
      countCol('purchases', 'Purchases'),
      currencyCol('purchases', 'Revenue'),
    ],
  },
  {
    nodeKey: 'sendGoogleAd',
    scope: 'googleAds',
    urlSlug: null,
    label: 'Google Ads',
    Icon: IconBrandGoogle,
    columns: [
      countCol('clicks', 'Clicks'),
      countCol('conversions', 'Conversions'),
      countCol('purchases', 'Purchases'),
      currencyCol('purchases', 'Revenue'),
    ],
  },
];

const formatStatValue = (col, cell, currency) => {
  const num = Number(cell?.[col.statKey] || 0);
  if (col.currency) {
    if (currency) {
      try {
        return num.toLocaleString(navigator.language, { style: 'currency', currency });
      } catch {
        /* unknown currency code → plain number */
      }
    }
    return num.toLocaleString(navigator.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return num.toLocaleString(navigator.language);
};

const collectMessagesForChannel = (nodes, nodeKey) => {
  const byMessageId = new Map();
  for (const node of nodes) {
    if (node.data?.custom?.key !== nodeKey) continue;
    const messageId = node.data?.custom?.values?.id;
    if (!messageId) continue;
    const stepLabel = node.data?.custom?.labels?.id || node.data?.label || node.id;
    const existing = byMessageId.get(messageId);
    if (existing) {
      existing.stepLabels.push(stepLabel);
    } else {
      byMessageId.set(messageId, {
        messageId,
        messageName: stepLabel,
        stepLabels: [stepLabel],
      });
    }
  }
  return Array.from(byMessageId.values());
};

const ChannelBreakdownTable = ({ campaign, startDate, endDate }) => {
  const { accountId, domainId } = useParams();
  const fetch = useFetch();
  const currency = useSelector((state) => state.domain.domain?.currency);
  const [response, setResponse] = useState();
  const [error, setError] = useState();

  const channelsInFlow = useMemo(() => {
    const nodes = campaign?.flow?.nodes || [];
    return CHANNELS.map((channel) => ({
      ...channel,
      messages: collectMessagesForChannel(nodes, channel.nodeKey),
    })).filter((channel) => channel.messages.length > 0);
  }, [campaign?.flow?.nodes]);

  useEffect(() => {
    if (!startDate || !endDate || !campaign?.id) return;
    const startTs = dateToStartOfDay(startDate, 'local');
    const endTs = dateToEndOfDay(endDate, 'local');
    setResponse(undefined);
    setError(undefined);
    let cancelled = false;
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/campaigns/${campaign.id}/messageStats/${encodeURIComponent(
        startTs
      )}/${encodeURIComponent(endTs)}`
    )
      .then((data) => {
        if (!cancelled) setResponse(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, domainId, fetch, startDate, endDate, campaign?.id]);

  // Columns merge across channels by (label, stat, statKey, currency). Same-shape
  // columns collapse (e.g. Email + Push "Purchases" both reading purchases.count),
  // while clashes like "Sent" backed by different `stat` fields stay separate.
  // Encounter order = column order.
  const { unifiedColumns, channelColumnLookup } = useMemo(() => {
    const seenColumnKeys = new Set();
    const columns = [];
    for (const channel of channelsInFlow) {
      for (const col of channel.columns) {
        const key = columnKey(col);
        if (seenColumnKeys.has(key)) continue;
        seenColumnKeys.add(key);
        columns.push({ ...col, key });
      }
    }
    const lookup = new Map(
      channelsInFlow.map((channel) => [
        channel.scope,
        new Map(channel.columns.map((col) => [columnKey(col), col])),
      ])
    );
    return { unifiedColumns: columns, channelColumnLookup: lookup };
  }, [channelsInFlow]);

  const messageNameByChannel = useMemo(() => {
    const map = new Map();
    if (!response) return map;
    for (const channel of channelsInFlow) {
      const messages = response?.[channel.scope]?.messages;
      if (!messages) continue;
      map.set(channel.scope, new Map(messages.map((m) => [m.id, m.name])));
    }
    return map;
  }, [response, channelsInFlow]);

  if (channelsInFlow.length === 0) {
    return (
      <Card padding="md">
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconChartBarOff size={48} color="var(--mantine-color-dimmed)" stroke={1.5} />
            <Title c="dimmed" order={4} ta="center">
              No messaging channels found in this flow
            </Title>
            <Text c="dimmed" size="sm" ta="center">
              Add an Email, SMS, WhatsApp, Viber, Push, Webhook, Facebook Ad or Google Ad step to see channel
              statistics.
            </Text>
          </Stack>
        </Center>
      </Card>
    );
  }

  const isLoading = !response && !error;

  return (
    <Card padding="md" withBorder>
      <Box pos="relative">
        <LoadingOverlay visible={isLoading} zIndex={2} overlayProps={{ blur: 1, backgroundOpacity: 0.4 }} />
        <Table striped withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: '1%', whiteSpace: 'nowrap' }}>Channel</Table.Th>
              <Table.Th>Message</Table.Th>
              {unifiedColumns.map((col) => (
                <Table.Th key={col.key} ta="right">
                  {col.label}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {channelsInFlow.flatMap((channel) =>
              channel.messages.map((message) => {
                const { Icon } = channel;
                const rowKey = `${channel.scope}:${message.messageId}`;
                const channelData = response?.[channel.scope];
                const channelError = channelData?.error;
                const stats = channelData?.statsByAction?.[message.messageId];
                const serverName = messageNameByChannel.get(channel.scope)?.get(message.messageId);
                const displayName = serverName || message.messageName;
                const messageHref = channel.urlSlug
                  ? `#/accounts/${accountId}/domains/${domainId}/${channel.urlSlug}/${message.messageId}/analytics`
                  : null;
                const nameInner = messageHref ? (
                  <Anchor href={messageHref} size="sm">
                    {displayName}
                  </Anchor>
                ) : (
                  <Text size="sm">{displayName}</Text>
                );
                const nameCell = (
                  <Tooltip label={`Used in step(s): ${message.stepLabels.join(', ')}`}>
                    {nameInner}
                  </Tooltip>
                );
                const channelCell = (
                  <Group gap="xs" wrap="nowrap">
                    <Icon size={18} />
                    <Text size="sm">{channel.label}</Text>
                  </Group>
                );

                if (error || channelError) {
                  return (
                    <Table.Tr key={rowKey}>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}>{channelCell}</Table.Td>
                      <Table.Td>{nameCell}</Table.Td>
                      <Table.Td colSpan={unifiedColumns.length}>
                        <Text c="red" size="sm">
                          Failed to load: {channelError || error.message || 'Unknown error'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                }
                const lookup = channelColumnLookup.get(channel.scope);
                return (
                  <Table.Tr key={rowKey}>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{channelCell}</Table.Td>
                    <Table.Td>{nameCell}</Table.Td>
                    {unifiedColumns.map((col) => {
                      const channelCol = lookup.get(col.key);
                      if (!channelCol) {
                        return <Table.Td key={col.key} ta="right" />;
                      }
                      const cell = stats?.[channelCol.stat];
                      return (
                        <Table.Td key={col.key} ta="right">
                          {formatStatValue(channelCol, cell, currency)}
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </Box>
    </Card>
  );
};

export default ChannelBreakdownTable;
export { CHANNELS, columnKey, formatStatValue };
